/**
 * StatusBadge - Work-order-specific badge molecule that pairs a status icon
 * with a human-readable label inside a Badge atom.
 *
 * Accepts a BadgeStatus value and automatically resolves the correct icon,
 * label text, and color variant. Use this whenever displaying work order
 * status in lists, cards, or detail views.
 *
 * @example
 * <StatusBadge status="in_progress" />
 * <StatusBadge status="overdue" className="ml-8px" />
 */
import React from 'react';
import { Badge, BadgeStatus } from '../../atoms';

/**
 * Props for the StatusBadge component.
 */
export interface StatusBadgeProps {
	/** Work order status — determines icon, label, and color */
	status: BadgeStatus;
	/** Additional CSS classes to merge onto the Badge */
	className?: string;
}

// --- Human-readable labels ---
// Maps internal status keys (snake_case) to display-friendly strings.
const statusLabels: Record<BadgeStatus, string> = {
	pending: 'Pending',
	in_progress: 'In Progress',
	completed: 'Completed',
	overdue: 'Overdue',
};

// --- Status icons ---
// Each status gets a small inline SVG icon for quick visual identification.
// All icons use aria-hidden="true" because the label text already conveys meaning.
const statusIcons: Record<BadgeStatus, React.ReactNode> = {
	// Solid circle — neutral "waiting" indicator
	pending: (
		<svg className="mr-4px h-12px w-12px" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
			<circle cx="10" cy="10" r="6" />
		</svg>
	),
	// Spinning partial circle — animated to convey active processing
	in_progress: (
		<svg className="mr-4px h-12px w-12px animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
			<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
			<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
		</svg>
	),
	// Checkmark — signals task is done
	completed: (
		<svg className="mr-4px h-12px w-12px" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
			<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
		</svg>
	),
	// Warning triangle — draws attention to time-sensitive overdue items
	overdue: (
		<svg className="mr-4px h-12px w-12px" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
			<path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
		</svg>
	),
};

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, className = '' }) => {
	return (
		// Delegates color/shape to Badge atom; passes status as the variant key.
		// dataRef overrides the atom marker so this keeps its molecule identity.
		<Badge variant={status} className={['status-badge', className].join(' ')} dataRef="molecule-status-badge">
			{/* Icon rendered before label for left-to-right visual flow */}
			{statusIcons[status]}
			{statusLabels[status]}
		</Badge>
	);
};

export default StatusBadge;
